/**
 * Training profile configuration schema and transformation helpers.
 * Centralizes the visual editor surface for simulator UI visibility,
 * editability, starting defaults, and fixed learner-facing values.
 *
 * @module trainingProfileConfiguration
 */

import { CITY_TEMPERATURES } from '../Simulations/HeatPumpSimulation/constants';
import buildingEnvelopeAverageUValueHelpImage from './HelpImages/Building_envelope_Average_U-value_help_img.png';
import buildingEraPickerHelpImage from './HelpImages/Building_era_picker_help_img.png';
import detailedEnvelopeToolHelpImage from './HelpImages/Detailed_envelope_tool_help_img.png';
import externalUnitWithCopButtonHelpImage from './HelpImages/External_unit_with_COP_button_help_img.png';
import heatingCurvePanelHelpImage from './HelpImages/Heating_curve_panel_help_img.png';
import heatFluxOverlayToolHelpImage from './HelpImages/Heat_flux_overlay_tool_help_img.png';
import radiatorGuideHelpImage from './HelpImages/Radiator_guide_help_img.png';
import radiatorRatingOverlayToolHelpImage from './HelpImages/Radiator_rating_overlay_tool_help_img.png';
import radiatorSizeHelpImage from './HelpImages/Radiator_size_help_img.png';
import radiatorTypeHelpImage from './HelpImages/Radiator type_help_img.png';
import roomRadiatorAreaHelpImage from './HelpImages/Room_radiator_area_help_img.png';
import roomTypeSelectorHelpImage from './HelpImages/Room_type_selector_help_img.png';
import roomValveThermostatHelpImage from './HelpImages/Room_valve_thermostat_help_img.png';
import supplyTemperatureHelpImage from './HelpImages/Supply_temperature_help_img.png';
import systemPressureHelpImage from './HelpImages/System_pressure_help_img.png';
import targetRoomTemperatureHelpImage from './HelpImages/Target_room_temperature_help_img.png';
import currentRoomTemperatureHelpImage from './HelpImages/Initial_Temp_help_img.png';
import tempCityHelpImage from './HelpImages/TempCity_help_img.png';
import tempClimateReferenceLinkHelpImage from './HelpImages/TempClimate_reference_link_help_img.png';
import temperatureMapPickerButtonHelpImage from './HelpImages/Temperature_Map_picker_button_help_img.png';
import tempInputHelpImage from './HelpImages/TempInput_help_img.png';
import totalBuildingAreaHelpImage from './HelpImages/Total_building_area_help_img.png';
import ventilationRateHelpImage from './HelpImages/Ventilation_rate_help_img.png';

const ROOM_TYPE_OPTIONS = [
  { value: 'living', label: 'Living room (20°C)', labelKey: 'trainingProfiles.configurationOptions.roomTypes.living' },
  { value: 'bathroom', label: 'Bathroom (24°C)', labelKey: 'trainingProfiles.configurationOptions.roomTypes.bathroom' },
];

const RADIATOR_TYPE_OPTIONS = ['10', '11', '21', '22', '33'].map(value => ({ value, label: `Type ${value}` }));

const RADIATOR_SIZE_OPTIONS = [40, 50, 60, 70, 80, 90, 100].map(value => ({
  value,
  label: `L ${value} cm, H 60 cm`,
}));

const CITY_OPTIONS = Object.entries(CITY_TEMPERATURES).map(([value, city]) => ({
  value,
  label: `${city.name} (${city.temperature}°C)`,
}));

/**
 * Structured configuration groups used by the training profile editor.
 */
export const TRAINING_PROFILE_CONFIGURATION_GROUPS = [
  {
    id: 'outdoor-climate',
    title: 'Outdoor climate',
    titleKey: 'trainingProfiles.configurationGroups.outdoorClimate.title',
    description: 'Control how learners choose the outdoor design condition and location context.',
    descriptionKey: 'trainingProfiles.configurationGroups.outdoorClimate.description',
    items: [
      {
        id: 'outdoorTemperature',
        controlKey: 'controlPanel.outdoorTemperature',
        valueKey: 'outdoorTemperature',
        title: 'Outdoor temperature input',
        titleKey: 'trainingProfiles.configurationGroups.outdoorClimate.items.outdoorTemperature.title',
        description: 'Manual outdoor temperature value shown in the main panel.',
        descriptionKey: 'trainingProfiles.configurationGroups.outdoorClimate.items.outdoorTemperature.description',
        helpImageSrc: tempInputHelpImage,
        supportsVisibility: true,
        supportsEditability: true,
        editor: { type: 'number', min: -10, max: 20, step: 1, unit: '°C', defaultValue: -10 },
      },
      {
        id: 'selectedCity',
        controlKey: 'controlPanel.selectedCity',
        valueKey: 'selectedCity',
        title: 'City selector',
        titleKey: 'trainingProfiles.configurationGroups.outdoorClimate.items.selectedCity.title',
        description: 'Preset city dropdown used to set climate design temperatures.',
        descriptionKey: 'trainingProfiles.configurationGroups.outdoorClimate.items.selectedCity.description',
        helpImageSrc: tempCityHelpImage,
        supportsVisibility: true,
        supportsEditability: true,
        editor: { type: 'select', options: CITY_OPTIONS, defaultValue: 'aachen' },
      },
      {
        id: 'mapSelectorButton',
        controlKey: 'controlPanel.mapSelectorButton',
        title: 'Map picker button',
        titleKey: 'trainingProfiles.configurationGroups.outdoorClimate.items.mapSelectorButton.title',
        description: 'Map icon that opens the outdoor temperature picker modal.',
        descriptionKey: 'trainingProfiles.configurationGroups.outdoorClimate.items.mapSelectorButton.description',
        helpImageSrc: temperatureMapPickerButtonHelpImage,
        supportsVisibility: true,
      },
      {
        id: 'climateMapLink',
        controlKey: 'controlPanel.climateMapLink',
        title: 'Climate reference link',
        titleKey: 'trainingProfiles.configurationGroups.outdoorClimate.items.climateMapLink.title',
        description: 'Question-mark shortcut to the external climate map reference.',
        descriptionKey: 'trainingProfiles.configurationGroups.outdoorClimate.items.climateMapLink.description',
        helpImageSrc: tempClimateReferenceLinkHelpImage,
        supportsVisibility: true,
      },
    ],
  },
  {
    id: 'building-envelope',
    title: 'Building envelope',
    titleKey: 'trainingProfiles.configurationGroups.buildingEnvelope.title',
    description: 'Define which building heat-loss inputs and supporting tools are available.',
    descriptionKey: 'trainingProfiles.configurationGroups.buildingEnvelope.description',
    items: [
      {
        id: 'envelopeUValue',
        controlKey: 'controlPanel.envelopeUValue',
        controlAliases: ['controlPanel.uValue'],
        valueKey: 'envelopeUValue',
        valueAliases: ['uValue'],
        title: 'Average U-value',
        titleKey: 'trainingProfiles.configurationGroups.buildingEnvelope.items.envelopeUValue.title',
        description: 'Overall building envelope heat transfer coefficient.',
        descriptionKey: 'trainingProfiles.configurationGroups.buildingEnvelope.items.envelopeUValue.description',
        helpImageSrc: buildingEnvelopeAverageUValueHelpImage,
        supportsVisibility: true,
        supportsEditability: true,
        editor: { type: 'number', min: 0.1, max: 5, step: 0.05, unit: 'W/(m²·K)', defaultValue: 0.35 },
      },
      {
        id: 'totalArea',
        controlKey: 'controlPanel.totalArea',
        valueKey: 'totalArea',
        title: 'Total building area',
        titleKey: 'trainingProfiles.configurationGroups.buildingEnvelope.items.totalArea.title',
        description: 'Combined heated surface area used for transmission losses.',
        descriptionKey: 'trainingProfiles.configurationGroups.buildingEnvelope.items.totalArea.description',
        helpImageSrc: totalBuildingAreaHelpImage,
        supportsVisibility: true,
        supportsEditability: true,
        editor: { type: 'number', min: 1, max: 1000, step: 1, unit: 'm²', defaultValue: 60 },
      },
      {
        id: 'airChangeRate',
        controlKey: 'controlPanel.airChangeRate',
        valueKey: 'airChangeRate',
        title: 'Ventilation rate',
        titleKey: 'trainingProfiles.configurationGroups.buildingEnvelope.items.airChangeRate.title',
        description: 'Air changes per hour used for ventilation heat losses.',
        descriptionKey: 'trainingProfiles.configurationGroups.buildingEnvelope.items.airChangeRate.description',
        helpImageSrc: ventilationRateHelpImage,
        supportsVisibility: true,
        supportsEditability: true,
        editor: { type: 'number', min: 0.25, max: 10, step: 0.25, unit: 'ACH', defaultValue: 0.5 },
      },
      {
        id: 'buildingParametersButton',
        controlKey: 'controlPanel.buildingParametersButton',
        title: 'Detailed envelope tool',
        titleKey: 'trainingProfiles.configurationGroups.buildingEnvelope.items.buildingParametersButton.title',
        description: 'Settings icon that opens roof, wall, and window parameter editing.',
        descriptionKey: 'trainingProfiles.configurationGroups.buildingEnvelope.items.buildingParametersButton.description',
        helpImageSrc: detailedEnvelopeToolHelpImage,
        supportsVisibility: true,
      },
      {
        id: 'buildingEraButton',
        controlKey: 'controlPanel.buildingEraButton',
        title: 'Building era picker',
        titleKey: 'trainingProfiles.configurationGroups.buildingEnvelope.items.buildingEraButton.title',
        description: 'Home icon that applies typical U-values from building eras.',
        descriptionKey: 'trainingProfiles.configurationGroups.buildingEnvelope.items.buildingEraButton.description',
        helpImageSrc: buildingEraPickerHelpImage,
        supportsVisibility: true,
      },
    ],
  },
  {
    id: 'radiators',
    title: 'Radiators',
    titleKey: 'trainingProfiles.configurationGroups.radiators.title',
    description: 'Manage radiator selection visibility, learner editing, and seeded values.',
    descriptionKey: 'trainingProfiles.configurationGroups.radiators.description',
    items: [
      {
        id: 'radiatorType',
        controlKey: 'controlPanel.radiatorType',
        valueKey: 'radiatorType',
        title: 'Radiator type',
        titleKey: 'trainingProfiles.configurationGroups.radiators.items.radiatorType.title',
        description: 'Panel type selector with compact radiator previews.',
        descriptionKey: 'trainingProfiles.configurationGroups.radiators.items.radiatorType.description',
        helpImageSrc: radiatorTypeHelpImage,
        supportsVisibility: true,
        supportsEditability: true,
        editor: { type: 'select', options: RADIATOR_TYPE_OPTIONS, defaultValue: '22' },
      },
      {
        id: 'radiatorSize',
        controlKey: 'controlPanel.radiatorSize',
        controlAliases: ['controlPanel.radiatorCount'],
        valueKey: 'radiatorSize',
        valueAliases: ['radiatorCount'],
        title: 'Radiator size',
        titleKey: 'trainingProfiles.configurationGroups.radiators.items.radiatorSize.title',
        description: 'Visible radiator length selector used in the current control panel.',
        descriptionKey: 'trainingProfiles.configurationGroups.radiators.items.radiatorSize.description',
        helpImageSrc: radiatorSizeHelpImage,
        supportsVisibility: true,
        supportsEditability: true,
        editor: { type: 'select', options: RADIATOR_SIZE_OPTIONS, defaultValue: 60 },
      },
      {
        id: 'radiatorGuideButton',
        controlKey: 'controlPanel.radiatorGuideButton',
        title: 'Radiator guide',
        titleKey: 'trainingProfiles.configurationGroups.radiators.items.radiatorGuideButton.title',
        description: 'Reference button for radiator type guidance.',
        descriptionKey: 'trainingProfiles.configurationGroups.radiators.items.radiatorGuideButton.description',
        helpImageSrc: radiatorGuideHelpImage,
        supportsVisibility: true,
      },
    ],
  },
  {
    id: 'room-supply',
    title: 'Room and supply settings',
    titleKey: 'trainingProfiles.configurationGroups.roomSupply.title',
    description: 'Define comfort targets, hydraulic conditions, and weather compensation tools.',
    descriptionKey: 'trainingProfiles.configurationGroups.roomSupply.description',
    items: [
      {
        id: 'desiredTemperature',
        controlKey: 'controlPanel.desiredTemperature',
        valueKey: 'desiredTemperature',
        title: 'Target room temperature',
        titleKey: 'trainingProfiles.configurationGroups.roomSupply.items.desiredTemperature.title',
        description: 'Main desired room temperature input.',
        descriptionKey: 'trainingProfiles.configurationGroups.roomSupply.items.desiredTemperature.description',
        helpImageSrc: targetRoomTemperatureHelpImage,
        supportsVisibility: true,
        supportsEditability: true,
        editor: { type: 'number', min: 10, max: 30, step: 1, unit: '°C', defaultValue: 20 },
      },
      {
        id: 'currentTemperature',
        valueKey: 'currentTemperature',
        title: 'Initial room temperature',
        titleKey: 'trainingProfiles.configurationGroups.roomSupply.items.currentTemperature.title',
        description: 'Room temperature at simulation start before learners press play.',
        descriptionKey: 'trainingProfiles.configurationGroups.roomSupply.items.currentTemperature.description',
        helpImageSrc: currentRoomTemperatureHelpImage,
        supportsFixedValue: false,
        editor: { type: 'number', min: 5, max: 30, step: 1, unit: '°C', defaultValue: 19 },
      },
      {
        id: 'selectedRoomType',
        controlKey: 'controlPanel.selectedRoomType',
        valueKey: 'selectedRoomType',
        title: 'Room type selector',
        titleKey: 'trainingProfiles.configurationGroups.roomSupply.items.selectedRoomType.title',
        description: 'Preset room selector that can auto-apply common room temperatures.',
        descriptionKey: 'trainingProfiles.configurationGroups.roomSupply.items.selectedRoomType.description',
        helpImageSrc: roomTypeSelectorHelpImage,
        supportsVisibility: true,
        supportsEditability: true,
        editor: { type: 'select', options: ROOM_TYPE_OPTIONS, defaultValue: 'living' },
      },
      {
        id: 'supplyTemperature',
        controlKey: 'controlPanel.supplyTemperature',
        valueKey: 'supplyTemperature',
        title: 'Supply temperature',
        titleKey: 'trainingProfiles.configurationGroups.roomSupply.items.supplyTemperature.title',
        description: 'Supply water temperature entering the radiator circuit.',
        descriptionKey: 'trainingProfiles.configurationGroups.roomSupply.items.supplyTemperature.description',
        helpImageSrc: supplyTemperatureHelpImage,
        supportsVisibility: true,
        supportsEditability: true,
        editor: { type: 'number', min: 20, max: 100, step: 1, unit: '°C', defaultValue: 45 },
      },
      {
        id: 'systemPressure',
        controlKey: 'controlPanel.systemPressure',
        valueKey: 'systemPressure',
        title: 'System pressure',
        titleKey: 'trainingProfiles.configurationGroups.roomSupply.items.systemPressure.title',
        description: 'Hydraulic pressure used for volumetric flow calculations.',
        descriptionKey: 'trainingProfiles.configurationGroups.roomSupply.items.systemPressure.description',
        helpImageSrc: systemPressureHelpImage,
        supportsVisibility: true,
        supportsEditability: true,
        editor: { type: 'number', min: 0.01, max: 100, step: 0.01, unit: 'bar', defaultValue: 0.1 },
      },
      {
        id: 'autoWeatherCompensation',
        controlKey: 'controlPanel.autoWeatherCompensation',
        controlAliases: ['controlPanel.heatingCurveButton'],
        valueKey: 'autoWeatherCompensation',
        title: 'Weather compensation controls',
        titleKey: 'trainingProfiles.configurationGroups.roomSupply.items.autoWeatherCompensation.title',
        description: 'Combined visibility for the always-clickable on/off auto-control toggle and the heating-curve panel button.',
        descriptionKey: 'trainingProfiles.configurationGroups.roomSupply.items.autoWeatherCompensation.description',
        helpImageSrc: heatingCurvePanelHelpImage,
        supportsVisibility: true,
        editor: { type: 'boolean', defaultValue: false },
      },
    ],
  },
  {
    id: 'advanced-values',
    title: 'Advanced seeded values',
    titleKey: 'trainingProfiles.configurationGroups.advancedValues.title',
    description: 'Preload hidden or modal-based simulator parameters without exposing raw JSON.',
    descriptionKey: 'trainingProfiles.configurationGroups.advancedValues.description',
    items: [
      {
        id: 'roofArea',
        valueKey: 'roofArea',
        title: 'Roof area',
        titleKey: 'trainingProfiles.configurationGroups.advancedValues.items.roofArea.title',
        description: 'Roof surface area used inside the detailed envelope tool.',
        descriptionKey: 'trainingProfiles.configurationGroups.advancedValues.items.roofArea.description',
        showImagePlaceholder: false,
        editor: { type: 'number', min: 0, max: 1000, step: 1, unit: 'm²', defaultValue: 20 },
      },
      {
        id: 'roofU',
        valueKey: 'roofU',
        title: 'Roof U-value',
        titleKey: 'trainingProfiles.configurationGroups.advancedValues.items.roofU.title',
        description: 'Roof heat transfer coefficient used for weighted envelope calculations.',
        descriptionKey: 'trainingProfiles.configurationGroups.advancedValues.items.roofU.description',
        showImagePlaceholder: false,
        editor: { type: 'number', min: 0, max: 5, step: 0.01, unit: 'W/(m²·K)', defaultValue: 0.2 },
      },
      {
        id: 'wallArea',
        valueKey: 'wallArea',
        title: 'Wall area',
        titleKey: 'trainingProfiles.configurationGroups.advancedValues.items.wallArea.title',
        description: 'Wall surface area used in detailed transmission calculations.',
        descriptionKey: 'trainingProfiles.configurationGroups.advancedValues.items.wallArea.description',
        showImagePlaceholder: false,
        editor: { type: 'number', min: 0, max: 1000, step: 1, unit: 'm²', defaultValue: 25 },
      },
      {
        id: 'wallU',
        valueKey: 'wallU',
        title: 'Wall U-value',
        titleKey: 'trainingProfiles.configurationGroups.advancedValues.items.wallU.title',
        description: 'Wall heat transfer coefficient used in weighted envelope calculations.',
        descriptionKey: 'trainingProfiles.configurationGroups.advancedValues.items.wallU.description',
        showImagePlaceholder: false,
        editor: { type: 'number', min: 0, max: 5, step: 0.01, unit: 'W/(m²·K)', defaultValue: 0.35 },
      },
      {
        id: 'windowArea',
        valueKey: 'windowArea',
        title: 'Window area',
        titleKey: 'trainingProfiles.configurationGroups.advancedValues.items.windowArea.title',
        description: 'Window surface area used inside the detailed envelope tool.',
        descriptionKey: 'trainingProfiles.configurationGroups.advancedValues.items.windowArea.description',
        showImagePlaceholder: false,
        editor: { type: 'number', min: 0, max: 1000, step: 1, unit: 'm²', defaultValue: 15 },
      },
      {
        id: 'windowU',
        valueKey: 'windowU',
        title: 'Window U-value',
        titleKey: 'trainingProfiles.configurationGroups.advancedValues.items.windowU.title',
        description: 'Window heat transfer coefficient used in weighted envelope calculations.',
        descriptionKey: 'trainingProfiles.configurationGroups.advancedValues.items.windowU.description',
        showImagePlaceholder: false,
        editor: { type: 'number', min: 0, max: 8, step: 0.01, unit: 'W/(m²·K)', defaultValue: 1.4 },
      },
      {
        id: 'valvePosition',
        valueKey: 'valvePosition',
        title: 'Valve position',
        titleKey: 'trainingProfiles.configurationGroups.advancedValues.items.valvePosition.title',
        description: 'Initial radiator valve position used for hydraulic output calculations.',
        descriptionKey: 'trainingProfiles.configurationGroups.advancedValues.items.valvePosition.description',
        helpImageSrc: roomValveThermostatHelpImage,
        editor: { type: 'number', min: 0, max: 1, step: 0.01, unit: '0-1', defaultValue: 3 / 7 },
      },
    ],
  },
  {
    id: 'visualization-surfaces',
    title: 'Visualization surfaces',
    titleKey: 'trainingProfiles.configurationGroups.visualizationSurfaces.title',
    description: 'Show or hide key visual teaching elements in the room, outdoor unit, and top toolbar.',
    descriptionKey: 'trainingProfiles.configurationGroups.visualizationSurfaces.description',
    items: [
      {
        id: 'roomValveThermostat',
        controlKey: 'roomUnit.valveThermostat',
        title: 'Room valve thermostat',
        titleKey: 'trainingProfiles.configurationGroups.visualizationSurfaces.items.roomValveThermostat.title',
        description: 'Interactive predefined valve and thermostat control inside the room unit.',
        descriptionKey: 'trainingProfiles.configurationGroups.visualizationSurfaces.items.roomValveThermostat.description',
        helpImageSrc: roomValveThermostatHelpImage,
        supportsVisibility: true,
      },
      {
        id: 'roomRadiatorArea',
        controlKey: 'roomUnit.radiatorArea',
        title: 'Room radiator area',
        titleKey: 'trainingProfiles.configurationGroups.visualizationSurfaces.items.roomRadiatorArea.title',
        description: 'Radiator body, local piping, flow indicators, and related room-side visualization.',
        descriptionKey: 'trainingProfiles.configurationGroups.visualizationSurfaces.items.roomRadiatorArea.description',
        helpImageSrc: roomRadiatorAreaHelpImage,
        supportsVisibility: true,
      },
      {
        id: 'externalUnit',
        controlKey: 'visualization.externalUnit',
        title: 'External unit with COP',
        titleKey: 'trainingProfiles.configurationGroups.visualizationSurfaces.items.externalUnit.title',
        description: 'Outdoor heat pump unit including the COP and operating readout panel.',
        descriptionKey: 'trainingProfiles.configurationGroups.visualizationSurfaces.items.externalUnit.description',
        helpImageSrc: externalUnitWithCopButtonHelpImage,
        supportsVisibility: true,
      },
      {
        id: 'toolbarRadiatorRatingOverlay',
        controlKey: 'toolbar.radiatorRatingOverlay',
        title: 'Radiator rating overlay tool',
        titleKey: 'trainingProfiles.configurationGroups.visualizationSurfaces.items.toolbarRadiatorRatingOverlay.title',
        description: 'Top-toolbar button and overlay for radiator heat output visualization.',
        descriptionKey: 'trainingProfiles.configurationGroups.visualizationSurfaces.items.toolbarRadiatorRatingOverlay.description',
        helpImageSrc: radiatorRatingOverlayToolHelpImage,
        supportsVisibility: true,
      },
      {
        id: 'toolbarHeatFluxOverlay',
        controlKey: 'toolbar.heatFluxOverlay',
        title: 'Heat flux overlay tool',
        titleKey: 'trainingProfiles.configurationGroups.visualizationSurfaces.items.toolbarHeatFluxOverlay.title',
        description: 'Top-toolbar button and overlay for transmission and ventilation loss visualization.',
        descriptionKey: 'trainingProfiles.configurationGroups.visualizationSurfaces.items.toolbarHeatFluxOverlay.description',
        helpImageSrc: heatFluxOverlayToolHelpImage,
        supportsVisibility: true,
      },
    ],
  },
];

const ALL_ITEMS = TRAINING_PROFILE_CONFIGURATION_GROUPS.flatMap(group => group.items);

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

const getFallbackValue = (item) => {
  if (item.editor?.defaultValue !== undefined) {
    return item.editor.defaultValue;
  }

  if (item.editor?.type === 'select') {
    return item.editor.options?.[0]?.value ?? null;
  }

  if (item.editor?.type === 'boolean') {
    return false;
  }

  return null;
};

const normalizeEditorValue = (item, value) => {
  if (value === undefined || value === null || value === '') {
    return getFallbackValue(item);
  }

  switch (item.editor?.type) {
    case 'number':
      return Number.isFinite(Number(value)) ? Number(value) : Number(getFallbackValue(item) ?? 0);
    case 'boolean':
      return Boolean(value);
    case 'select':
      return String(value);
    default:
      return value;
  }
};

const getSourceValue = (source, keys = []) => {
  for (const key of keys) {
    if (key && hasOwn(source, key)) {
      return source[key];
    }
  }
  return undefined;
};

const getControlConfiguration = (uiConfiguration, item) => {
  const controls = uiConfiguration?.controls || uiConfiguration || {};
  const controlKeys = [
    item.controlKey,
    ...(item.controlAliases || []),
    item.valueKey,
    ...(item.valueAliases || []),
  ].filter(Boolean);

  return getSourceValue(controls, controlKeys);
};

/**
 * Creates the visual configuration draft used by the editor from stored JSON payloads.
 * @param {Object} payloads - Existing profile payloads.
 * @returns {Object} Editor draft keyed by configuration item id.
 */
export const createTrainingProfileConfigurationDraft = (payloads = {}) => {
  const uiConfiguration = payloads.uiConfiguration || null;
  const simulationDefaults = payloads.simulationDefaults || null;
  const fixedValues = payloads.fixedValues || null;

  return ALL_ITEMS.reduce((draft, item) => {
    const valueKeys = [item.valueKey, ...(item.valueAliases || [])].filter(Boolean);
    const controlConfiguration = getControlConfiguration(uiConfiguration, item) || {};
    const rawDefaultValue = getSourceValue(simulationDefaults, valueKeys);
    const rawFixedValue = getSourceValue(fixedValues, valueKeys);
    const supportsFixedValue = item.supportsFixedValue !== false;
    const fixedValuePayload = rawFixedValue && typeof rawFixedValue === 'object' && hasOwn(rawFixedValue, 'value')
      ? rawFixedValue
      : null;
    const effectiveDefaultValue = rawDefaultValue !== undefined
      ? rawDefaultValue
      : (!supportsFixedValue ? (fixedValuePayload?.value ?? rawFixedValue) : undefined);

    const fixedEnabled = supportsFixedValue && rawFixedValue !== undefined;
    const visible = item.supportsVisibility
      ? !(controlConfiguration?.visible === false || controlConfiguration?.hidden === true || controlConfiguration?.displayMode === 'hidden')
      : true;
    const editable = item.supportsEditability
      ? visible && !(controlConfiguration?.editable === false || controlConfiguration?.locked === true || fixedEnabled)
      : true;

    draft[item.id] = {
      visible,
      editable,
      defaultEnabled: effectiveDefaultValue !== undefined,
      defaultValue: normalizeEditorValue(item, effectiveDefaultValue),
      fixedEnabled,
      fixedValue: normalizeEditorValue(item, supportsFixedValue ? (fixedValuePayload?.value ?? rawFixedValue) : undefined),
    };

    return draft;
  }, {});
};

/**
 * Builds the UI configuration payload expected by the backend.
 * @param {Object} draft - Editor draft.
 * @returns {Object|null} UI configuration payload or null.
 */
export const buildTrainingProfileUiConfiguration = (draft) => {
  const controls = ALL_ITEMS.reduce((nextControls, item) => {
    const controlKeys = [item.controlKey, ...(item.controlAliases || [])].filter(Boolean);
    if (controlKeys.length === 0) {
      return nextControls;
    }

    const state = draft[item.id];
    if (!state) {
      return nextControls;
    }

    const configuration = {};
    const isVisible = state.visible !== false;

    if (item.supportsVisibility) {
      configuration.visible = isVisible;
    }

    if (item.supportsEditability) {
      configuration.editable = isVisible && !state.fixedEnabled && state.editable !== false;
    }

    controlKeys.forEach((controlKey) => {
      nextControls[controlKey] = { ...configuration };
    });
    return nextControls;
  }, {});

  return Object.keys(controls).length > 0 ? { controls } : null;
};

/**
 * Builds the simulation defaults payload expected by the backend.
 * @param {Object} draft - Editor draft.
 * @returns {Object|null} Simulation defaults payload or null.
 */
export const buildTrainingProfileSimulationDefaults = (draft) => {
  const defaults = ALL_ITEMS.reduce((nextDefaults, item) => {
    const state = draft[item.id];
    if (!item.valueKey || !state?.defaultEnabled) {
      return nextDefaults;
    }

    nextDefaults[item.valueKey] = normalizeEditorValue(item, state.defaultValue);
    return nextDefaults;
  }, {});

  return Object.keys(defaults).length > 0 ? defaults : null;
};

/**
 * Builds the fixed values payload expected by the backend.
 * @param {Object} draft - Editor draft.
 * @returns {Object|null} Fixed values payload or null.
 */
export const buildTrainingProfileFixedValues = (draft) => {
  const fixedValues = ALL_ITEMS.reduce((nextFixedValues, item) => {
    const state = draft[item.id];
    if (!item.valueKey || item.supportsFixedValue === false || !state?.fixedEnabled) {
      return nextFixedValues;
    }

    nextFixedValues[item.valueKey] = {
      value: normalizeEditorValue(item, state.fixedValue),
      locked: true,
      displayMode: state.visible === false ? 'hidden' : 'visibleLocked',
    };
    return nextFixedValues;
  }, {});

  return Object.keys(fixedValues).length > 0 ? fixedValues : null;
};